import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import {
  CATEGORIES,
  counterAverages,
  formatBytes,
  formatCount,
  formatMs,
  frameHistory,
  frameStats,
  gpuPasses,
  percentile,
  selfTimes,
  sortSystems,
  topSystems,
  categoryOf,
  type HistoryPoint,
  type ProfilerFrame,
  type SystemSortKey,
} from '../../lib/profiler-model.js';
import { useKeyHint } from '../../lib/shortcuts/index.js';
import { EmptyState, IconButton, MenuButton, SelectField, Toggle } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { toChromeTrace } from '../../lib/profile-summary.js';
import { frames, useProfiler, type ProfilerTarget } from './profiler-store.js';
import './profiler.css';

/**
 * Profiler (Unity's Profiler window): records the Play view while it runs,
 * else the Scene view. Frame history graph (stacked CPU categories, GPU
 * line; click a frame, drag a range), the selected frame's CPU / GPU
 * timeline, the Top Systems table over the selection and a counters strip.
 */

const CATEGORY_COLOR = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.color]));
const GPU_COLOR = '#e879f9';
const BAR_W = 3;

function download(name: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function stamp(): string {
  return new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
}

/** Canvas sized to its box in device pixels; redraws on resize. */
function useCanvas(draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void, deps: unknown[]) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || size.w <= 0 || size.h <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = Math.round(size.w * dpr);
    el.height = Math.round(size.h * dpr);
    const ctx = el.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);
    draw(ctx, size.w, size.h);
  }, [size, ...deps]);
  return { ref, size };
}

function cssVar(el: Element | null, name: string, fallback: string): string {
  if (!el) return fallback;
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

/* ------------------------------------------------------------------ */
/* Frame history graph                                                 */

function HistoryGraph({
  points,
  selected,
  range,
  onSelect,
  onRange,
}: {
  points: HistoryPoint[];
  selected: number | null;
  range: [number, number] | null;
  onSelect: (seq: number) => void;
  onRange: (range: [number, number] | null) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const drag = useRef<{ start: number; moved: boolean } | null>(null);
  const [width, setWidth] = useState(600);
  const visible = Math.max(10, Math.floor(width / BAR_W));
  // Window: the newest frames, or the ones around the selection.
  const end = useMemo(() => {
    if (selected === null) return points.length;
    const i = points.findIndex((p) => p.seq === selected);
    if (i < 0) return points.length;
    return Math.min(points.length, Math.max(i + 1 + Math.floor(visible / 4), visible));
  }, [points, selected, visible]);
  const shown = points.slice(Math.max(0, end - visible), end);
  const scale = useMemo(() => {
    const ms = shown.map((p) => Math.max(p.ms, p.gpuMs ?? 0));
    return Math.max(8, (percentile(ms, 98) || 0) * 1.2);
  }, [shown]);

  const { ref, size } = useCanvas(
    (ctx, w, h) => {
      setWidth(w);
      const text = cssVar(ref.current, '--muted', '#868b96');
      const grid = cssVar(ref.current, '--border', '#2a2d34');
      const accent = cssVar(ref.current, '--accent', '#5b8cff');
      const y = (ms: number) => h - (Math.min(ms, scale) / scale) * (h - 12);
      // Range highlight.
      if (range) {
        const a = shown.findIndex((p) => p.seq >= range[0]);
        let bIdx = -1;
        shown.forEach((p, i) => {
          if (p.seq <= range[1]) bIdx = i;
        });
        if (a >= 0 && bIdx >= a) {
          ctx.fillStyle = 'rgb(91 140 255 / 14%)';
          ctx.fillRect(a * BAR_W, 0, (bIdx - a + 1) * BAR_W, h);
        }
      }
      shown.forEach((p, i) => {
        let acc = 0;
        p.stack.forEach((v, c) => {
          if (v <= 0) return;
          const top = y(acc + v);
          const bottom = y(acc);
          ctx.fillStyle = CATEGORIES[c]!.color;
          ctx.fillRect(i * BAR_W, top, BAR_W - 0.5, Math.max(0.5, bottom - top));
          acc += v;
        });
      });
      // GPU line.
      ctx.strokeStyle = GPU_COLOR;
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      let pen = false;
      shown.forEach((p, i) => {
        if (p.gpuMs === null) {
          pen = false;
          return;
        }
        const x = i * BAR_W + BAR_W / 2;
        if (pen) ctx.lineTo(x, y(p.gpuMs));
        else ctx.moveTo(x, y(p.gpuMs));
        pen = true;
      });
      ctx.stroke();
      // 60 / 30 fps guides.
      ctx.font = '10px ui-monospace, monospace';
      for (const [ms, label] of [
        [1000 / 60, '16.7ms (60fps)'],
        [1000 / 30, '33.3ms (30fps)'],
      ] as const) {
        if (ms > scale) continue;
        ctx.strokeStyle = grid;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(0, y(ms) + 0.5);
        ctx.lineTo(w, y(ms) + 0.5);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = text;
        ctx.fillText(label, 4, y(ms) - 3);
      }
      ctx.fillStyle = text;
      ctx.fillText(formatMs(scale), w - 56, 10);
      // Selection.
      const si = shown.findIndex((p) => p.seq === selected);
      if (si >= 0) {
        ctx.fillStyle = accent;
        ctx.fillRect(si * BAR_W - 0.5, 0, Math.max(1.5, BAR_W - 0.5), h);
      }
    },
    [shown, scale, selected, range],
  );

  const seqAt = (e: ReactPointerEvent<HTMLCanvasElement>): number | null => {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.floor((e.clientX - r.left) / BAR_W);
    return shown[Math.min(shown.length - 1, Math.max(0, i))]?.seq ?? null;
  };
  const hovered = hover === null ? null : points.find((p) => p.seq === hover) ?? null;

  return (
    <div className="prof-history">
      <canvas
        ref={ref}
        className="prof-canvas"
        onPointerDown={(e) => {
          const seq = seqAt(e);
          if (seq === null) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drag.current = { start: seq, moved: false };
          onSelect(seq);
        }}
        onPointerMove={(e) => {
          const seq = seqAt(e);
          setHover(seq);
          const d = drag.current;
          if (d && seq !== null && seq !== d.start) {
            d.moved = true;
            onRange([Math.min(d.start, seq), Math.max(d.start, seq)]);
          }
        }}
        onPointerUp={(e) => {
          const d = drag.current;
          drag.current = null;
          if (d && !d.moved) {
            const seq = seqAt(e);
            onRange(null);
            if (seq !== null) onSelect(seq);
          }
        }}
        onPointerLeave={() => setHover(null)}
      />
      {hovered && size.w > 0 && (
        <div className="prof-history-tip">
          #{hovered.seq} · CPU {formatMs(hovered.ms)}
          {hovered.gpuMs !== null && ` · GPU ${formatMs(hovered.gpuMs)}`}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Timeline of one frame                                               */

interface Bar {
  name: string;
  start: number;
  ms: number;
  self: number | null;
  row: number;
  color: string;
  gpu: boolean;
}

const ROW_H = 18;

function layoutBars(frame: ProfilerFrame): { bars: Bar[]; cpuRows: number; rows: number; span: number } {
  const self = selfTimes(frame.cpu);
  const bars: Bar[] = frame.cpu.map((s, i) => ({
    name: s.name,
    start: s.start,
    ms: s.ms,
    self: self[i]!,
    row: s.depth,
    color: CATEGORY_COLOR[categoryOf(s.name)] ?? '#64748b',
    gpu: false,
  }));
  const cpuRows = Math.max(1, ...frame.cpu.map((s) => s.depth + 1));
  // GPU passes: first row (from the GPU track's top) that is free at their start.
  const ends: number[] = [];
  for (const p of frame.gpu ?? []) {
    let row = ends.findIndex((e) => e <= p.start + 1e-6);
    if (row < 0) row = ends.push(0) - 1;
    ends[row] = p.start + p.ms;
    bars.push({ name: p.name, start: p.start, ms: p.ms, self: null, row: cpuRows + 1 + row, color: GPU_COLOR, gpu: true });
  }
  const gpuEnd = Math.max(0, ...ends);
  return { bars, cpuRows, rows: cpuRows + (ends.length ? ends.length + 1 : 0), span: Math.max(frame.ms, gpuEnd, 0.001) };
}

function Timeline({ frame }: { frame: ProfilerFrame | null }) {
  const layout = useMemo(() => (frame ? layoutBars(frame) : null), [frame]);
  const [view, setView] = useState<[number, number] | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number; bar: Bar } | null>(null);
  const pan = useRef<{ x: number; view: [number, number] } | null>(null);
  useEffect(() => setView(null), [frame?.seq]);
  const span = layout?.span ?? 1;
  const [v0, v1] = view ?? [0, span];

  const { ref, size } = useCanvas(
    (ctx, w, h) => {
      if (!layout) return;
      const text = cssVar(ref.current, '--muted', '#868b96');
      const grid = cssVar(ref.current, '--border-soft', '#202227');
      const fg = '#0d0e11';
      const x = (ms: number) => ((ms - v0) / (v1 - v0)) * w;
      // Axis ticks.
      const step = niceStep((v1 - v0) / Math.max(1, w / 90));
      ctx.font = '10px ui-monospace, monospace';
      for (let t = Math.ceil(v0 / step) * step; t <= v1; t += step) {
        ctx.fillStyle = grid;
        ctx.fillRect(Math.round(x(t)), 0, 1, h);
        ctx.fillStyle = text;
        ctx.fillText(formatMs(t), x(t) + 3, 10);
      }
      if (layout.rows > layout.cpuRows) {
        ctx.fillStyle = text;
        ctx.fillText('GPU', 4, 14 + (layout.cpuRows + 1) * ROW_H - 4);
      }
      for (const b of layout.bars) {
        const x0 = x(b.start);
        const x1 = x(b.start + b.ms);
        if (x1 < 0 || x0 > w) continue;
        const y = 14 + b.row * ROW_H;
        const bw = Math.max(1, x1 - x0);
        ctx.fillStyle = b.color;
        ctx.fillRect(x0, y, bw - (bw > 3 ? 1 : 0), ROW_H - 2);
        if (bw > 40) {
          ctx.save();
          ctx.beginPath();
          ctx.rect(Math.max(0, x0), y, bw, ROW_H - 2);
          ctx.clip();
          ctx.fillStyle = fg;
          ctx.font = '11px system-ui, sans-serif';
          ctx.fillText(`${b.name} ${formatMs(b.ms)}`, Math.max(0, x0) + 4, y + 12);
          ctx.restore();
        }
      }
    },
    [layout, v0, v1],
  );

  if (!frame || !layout) {
    return <div className="prof-timeline prof-empty muted">Select a frame in the graph to see its timeline.</div>;
  }
  const height = 14 + layout.rows * ROW_H + 6;
  const toMs = (clientX: number, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    return v0 + ((clientX - r.left) / Math.max(1, r.width)) * (v1 - v0);
  };
  return (
    <div className="prof-timeline">
      <div className="prof-timeline-head">
        <span>
          Frame #{frame.seq} · CPU {formatMs(frame.ms)}
          {frame.gpuMs !== null && ` · GPU ${formatMs(frame.gpuMs)}`}
          {frame.interval > 0 && ` · ${(1000 / frame.interval).toFixed(0)} fps`}
        </span>
        <span className="muted">Wheel to zoom, drag to pan</span>
        <IconButton icon="refresh" title="Reset zoom" size={13} disabled={!view} onClick={() => setView(null)} />
      </div>
      <div className="prof-timeline-body" style={{ height }}>
        <canvas
          ref={ref}
          className="prof-canvas"
          onWheel={(e) => {
            const at = toMs(e.clientX, e.currentTarget);
            const k = e.deltaY > 0 ? 1.25 : 0.8;
            const a = Math.max(0, at - (at - v0) * k);
            const b = Math.min(span, at + (v1 - at) * k);
            setView(b - a >= span * 0.999 ? null : [a, Math.max(a + 0.001, b)]);
          }}
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            pan.current = { x: e.clientX, view: [v0, v1] };
          }}
          onPointerUp={() => (pan.current = null)}
          onPointerMove={(e) => {
            const p = pan.current;
            if (p) {
              const d = ((e.clientX - p.x) / Math.max(1, size.w)) * (p.view[1] - p.view[0]);
              const len = p.view[1] - p.view[0];
              const a = Math.min(Math.max(0, p.view[0] - d), span - len);
              if (len < span) setView([a, a + len]);
              return;
            }
            const r = e.currentTarget.getBoundingClientRect();
            const ms = toMs(e.clientX, e.currentTarget);
            const row = Math.floor((e.clientY - r.top - 14) / ROW_H);
            const bar = layout.bars.find((b) => b.row === row && ms >= b.start && ms <= b.start + b.ms);
            setTip(bar ? { x: e.clientX - r.left, y: e.clientY - r.top, bar } : null);
          }}
          onPointerLeave={() => setTip(null)}
        />
        {tip && (
          <div className="prof-tip" style={{ left: Math.min(tip.x + 12, Math.max(0, size.w - 220)), top: tip.y + 14 }}>
            <strong>{tip.bar.name}</strong>
            <span>
              {tip.bar.gpu ? 'GPU pass' : 'CPU'} · {formatMs(tip.bar.ms)}
            </span>
            {tip.bar.self !== null && <span>Self {formatMs(tip.bar.self)}</span>}
            <span className="muted">Starts at {formatMs(tip.bar.start)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function niceStep(raw: number): number {
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1e-6))));
  for (const m of [1, 2, 5, 10]) if (raw <= m * pow) return m * pow;
  return 10 * pow;
}

/* ------------------------------------------------------------------ */
/* Tables                                                              */

const COLUMNS: { key: SystemSortKey; label: string; title: string }[] = [
  { key: 'name', label: 'System', title: 'Profiler scope' },
  { key: 'avgMs', label: 'Avg', title: 'Average time per frame of the selection' },
  { key: 'maxMs', label: 'Max', title: 'Worst frame' },
  { key: 'selfAvgMs', label: 'Self', title: 'Average time without nested scopes' },
  { key: 'calls', label: 'Calls', title: 'Calls per frame' },
  { key: 'share', label: '%', title: 'Share of the frame time' },
];

function SystemsTable({ selection }: { selection: readonly ProfilerFrame[] }) {
  const [sort, setSort] = useState<{ key: SystemSortKey; desc: boolean }>({ key: 'avgMs', desc: true });
  const rows = useMemo(() => sortSystems(topSystems(selection), sort.key, sort.desc), [selection, sort]);
  const gpu = useMemo(() => gpuPasses(selection), [selection]);
  if (!selection.length) return <div className="prof-empty muted">No frames selected.</div>;
  return (
    <div className="prof-tables">
      <table className="prof-table">
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th
                key={c.key}
                title={c.title}
                className={sort.key === c.key ? 'is-sorted' : ''}
                onClick={() => setSort((s) => ({ key: c.key, desc: s.key === c.key ? !s.desc : c.key !== 'name' }))}
              >
                {c.label}
                {sort.key === c.key && <Icon name={sort.desc ? 'chevronDown' : 'chevronUp'} size={10} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="prof-name">
                <i style={{ background: CATEGORY_COLOR[r.category] }} />
                {r.name}
              </td>
              <td>{formatMs(r.avgMs)}</td>
              <td>{formatMs(r.maxMs)}</td>
              <td>{formatMs(r.selfAvgMs)}</td>
              <td>{r.callsPerFrame.toFixed(r.callsPerFrame % 1 ? 1 : 0)}</td>
              <td>{(r.share * 100).toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {gpu.length > 0 && (
        <table className="prof-table">
          <thead>
            <tr>
              <th>GPU pass</th>
              <th>Avg</th>
              <th>Max</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {gpu.map((p) => (
              <tr key={p.name}>
                <td className="prof-name">
                  <i style={{ background: GPU_COLOR }} />
                  {p.name}
                </td>
                <td>{formatMs(p.avgMs)}</td>
                <td>{formatMs(p.maxMs)}</td>
                <td>{p.count.toFixed(p.count % 1 ? 1 : 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Counters                                                            */

function Counters({ selection, all }: { selection: readonly ProfilerFrame[]; all: readonly ProfilerFrame[] }) {
  const c = useMemo(() => counterAverages(selection), [selection]);
  const stats = useMemo(() => frameStats(selection.length > 1 ? selection : all.slice(-120)), [selection, all]);
  const one = selection.length === 1 ? selection[0]! : null;
  const items: [string, string, string][] = [
    ['FPS', stats.fps ? stats.fps.toFixed(0) : '–', 'Frames per second (from frame intervals)'],
    ['CPU', formatMs(one ? one.ms : stats.avgMs), 'CPU time (update + render encode)'],
    ['p95', formatMs(stats.p95), '95th percentile CPU frame time'],
    ['GPU', formatMs(one ? one.gpuMs : stats.gpuAvgMs), 'GPU time (timestamp queries)'],
    ['Draws', formatCount(c.drawCalls), 'Draw calls'],
    ['Tris', formatCount(c.triangles), 'Triangles'],
    ['Visible', formatCount(c.visibleDraws), 'Draws after culling'],
    ['Culled', formatCount(c.culledDraws), 'Draws skipped by culling'],
    ['Entities', formatCount(c.entities), 'Entities in the world'],
    ['GPU mem', formatBytes(c.gpuMemoryBytes), `Buffers ${formatBytes(c.gpuBufferBytes)}, textures ${formatBytes(c.gpuTextureBytes)} (estimate)`],
    ['WASM', formatBytes(c.wasmMemoryBytes), 'WebAssembly memory'],
  ];
  return (
    <div className="prof-counters">
      {items.map(([label, value, title]) => (
        <div key={label} className="prof-counter" title={title}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Panel                                                               */

const TARGETS: { value: ProfilerTarget; label: string }[] = [
  { value: 'auto', label: 'Auto (Play when running)' },
  { value: 'play', label: 'Play view' },
  { value: 'scene', label: 'Scene view' },
];

export function ProfilerPanel() {
  const s = useProfiler();
  const recordKey = useKeyHint('profiler.record');
  const fileRef = useRef<HTMLInputElement>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // `frames` lives outside React; `version` says when it changed.
  const all = useMemo(() => [...frames.frames()], [s.version]);
  const points = useMemo(() => frameHistory(all), [all]);
  const selectedFrame = s.selected === null ? null : all.find((f) => f.seq === s.selected) ?? null;
  const selection = useMemo(() => {
    if (s.range) return all.filter((f) => f.seq >= s.range![0] && f.seq <= s.range![1]);
    return selectedFrame ? [selectedFrame] : all.slice(-120);
  }, [all, s.range, selectedFrame]);

  const empty = all.length === 0;
  const exportItems = [
    { id: 'json', label: 'Capture (.json)', icon: 'file' as const, run: () => download(`profile-${stamp()}.json`, s.exportJson(), 'application/json') },
    { id: 'csv', label: s.range ? 'Selected range as CSV' : 'All frames as CSV', icon: 'list' as const, run: () => download(`profile-${stamp()}.csv`, s.exportCsv(), 'text/csv') },
    { id: 'chrome', label: 'Chrome trace (.json)', icon: 'file' as const, run: () => download(`trace-${stamp()}.json`, toChromeTrace(all), 'application/json') },
  ];

  return (
    <div className="prof-panel">
      <div className="prof-toolbar">
        <IconButton
          icon={s.recording ? 'pause' : 'target'}
          title={s.recording ? 'Pause recording' : 'Record'}
          shortcut={recordKey}
          active={s.recording}
          className={s.recording ? 'prof-rec' : ''}
          onClick={() => s.record(!s.recording)}
        />
        <IconButton icon="trash" title="Clear" disabled={empty && !s.loaded} onClick={() => s.clear()} />
        <div className="prof-target">
          <SelectField value={s.target} options={TARGETS} onChange={(v) => s.setTarget(v as ProfilerTarget)} />
        </div>
        <Toggle checked={s.gpu} onChange={(on) => s.setGpu(on)} label="GPU" />
        <IconButton icon="chevronRight" title="Follow the latest frame" active={s.follow} disabled={!s.recording} onClick={() => s.setFollow(!s.follow)} />
        <span className="prof-spacer" />
        <span className="prof-status muted">
          {s.loaded
            ? `Capture${s.loaded.target ? ` (${s.loaded.target})` : ''} · ${all.length} frames`
            : s.recording
              ? `Recording ${s.source ?? ''} view · ${all.length} frames`
              : `${all.length} frames`}
          {s.status && s.gpu && !s.status.gpu.supported && ' · GPU timing unsupported'}
          {s.range && ` · range ${s.range[0]}–${s.range[1]}`}
        </span>
        <MenuButton items={exportItems} className="ibtn" title="Export">
          <Icon name="arrowDown" size={15} />
        </MenuButton>
        <IconButton icon="folderOpen" title="Load capture…" onClick={() => fileRef.current?.click()} />
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          hidden
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file) return;
            try {
              s.load(await file.text());
              setLoadError(null);
            } catch (error) {
              setLoadError(error instanceof Error ? error.message : String(error));
            }
          }}
        />
      </div>
      {(s.error || loadError) && <div className="prof-error">{loadError ?? s.error}</div>}
      {empty ? (
        <EmptyState icon="gauge" title={s.recording ? 'Waiting for frames…' : 'Profiler'}>
          Press Record to profile the Play view while the game runs, or the Scene view otherwise. Load a saved capture with the folder button.
        </EmptyState>
      ) : (
        <>
          <Counters selection={selection} all={all} />
          <div className="prof-legend">
            {CATEGORIES.map((c) => (
              <span key={c.id}>
                <i style={{ background: c.color }} />
                {c.label}
              </span>
            ))}
            <span>
              <i style={{ background: GPU_COLOR }} />
              GPU
            </span>
          </div>
          <HistoryGraph points={points} selected={s.selected} range={s.range} onSelect={s.select} onRange={s.setRange} />
          <div className="prof-bottom">
            <Timeline frame={selectedFrame} />
            <SystemsTable selection={selection} />
          </div>
        </>
      )}
    </div>
  );
}
